import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import colors from '../styles/colors';
import { getMenuData } from '../data/menuData';
import { useMultiLanguage } from '../hooks/useMultiLanguage';
import MenuLanguageSwitcher from './MenuLanguageSwitcher';

const MenuPageContainer = styled.div`
  min-height: 100vh;
  background: ${colors.background.light};
  padding: 2rem 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 4rem;

  h1 {
    font-size: 3rem;
    font-weight: 700;
    color: ${colors.text.primary};
    margin-bottom: 1rem;
    position: relative;
    display: inline-block;

    &::after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 4px;
      background: ${colors.primary.green};
      border-radius: 2px;
    }
  }

  p {
    font-size: 1.2rem;
    color: ${colors.text.secondary};
    max-width: 600px;
    margin: 0 auto;
  }

  @media (max-width: 768px) {
    h1 {
      font-size: 2.5rem;
    }
    
    p {
      font-size: 1.1rem;
    }
  }
`;

const MenuSection = styled(motion.section)`
  margin-bottom: 4rem;
  background: white;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  border: 2px solid ${colors.primary.green}20;

  @media (max-width: 768px) {
    padding: 2rem;
    margin-bottom: 2rem;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;

  h2 {
    font-size: 2.2rem;
    font-weight: 700;
    color: ${colors.primary.green};
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 1.1rem;
    color: ${colors.text.secondary};
  }
`;

const MenuGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const MenuItem = styled(motion.div)`
  background: ${colors.background.light};
  border-radius: 15px;
  padding: 1.5rem;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    border-color: ${colors.primary.green};
    transform: translateY(-5px);
    box-shadow: 0 10px 25px rgba(0,0,0,0.1);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${colors.primary.green}, ${colors.primary.blue});
    border-radius: 15px 15px 0 0;
  }
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;

  h3 {
    font-size: 1.4rem;
    font-weight: 600;
    color: ${colors.text.primary};
    margin: 0;
    flex: 1;
  }

  .price {
    font-size: 1.3rem;
    font-weight: 700;
    color: ${colors.primary.green};
    margin-left: 1rem;
  }
`;

const ItemNote = styled.p`
  color: ${colors.primary.blue};
  font-style: italic;
  font-size: 0.9rem;
  margin: 0;
  font-weight: 500;
`;

const MultiLanguageText = styled.div`
  .main-text {
    font-size: 0.95rem;
    color: ${colors.text.secondary};
    line-height: 1.6;
    margin-bottom: 0.3rem;
  }
  
  .secondary-text {
    font-size: 0.8rem;
    color: ${colors.text.secondary}80;
    line-height: 1.4;
    margin-bottom: 0.3rem;
  }
  
  .english-text {
    font-size: 0.7rem;
    color: ${colors.text.secondary}50;
    line-height: 1.3;
    font-style: italic;
    margin-bottom: 0.5rem;
  }
`;

const SectionNote = styled.div`
  text-align: center;
  margin-top: 2rem;
  padding: 1.5rem;
  background: ${colors.primary.green}10;
  border-radius: 10px;
  border-left: 4px solid ${colors.primary.green};

  p {
    color: ${colors.text.primary};
    font-weight: 500;
    margin: 0;
    font-size: 1rem;
  }
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: ${colors.primary.green};
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
  margin-bottom: 2rem;
  
  &:hover {
    background: ${colors.primary.blue};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  &::before {
    content: '←';
    font-size: 1.2rem;
  }
`;

const BackButtonContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-bottom: 2rem;
`;

const MenuPage = ({ menuType, title, subtitle }) => {
  const { t } = useTranslation();
  const { getTranslations } = useMultiLanguage();
  const menuData = getMenuData(menuType);
  
  if (!menuData) {
    return (
      <MenuPageContainer>
        <Container>
          <Header>
            <h1>Cardápio não encontrado</h1>
            <p>O cardápio solicitado não está disponível.</p>
          </Header>
        </Container>
      </MenuPageContainer>
    );
  }

  return (
    <MenuPageContainer>
      <Container>
        <BackButtonContainer>
          <BackButton to="/">
            {t('menu.backToSite', { defaultValue: 'Voltar ao Site' })}
          </BackButton>
        </BackButtonContainer>
        <MenuLanguageSwitcher />
        <Header>
          <h1>{t('menu.lunchMenuTitle')}</h1>
          <p>{t('menu.lunchMenuSubtitle')}</p>
        </Header>

        <MenuSection
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <SectionHeader>
            <h2>{t('menu.pratos')}</h2>
            <p>{t('menu.pratosSubtitle')}</p>
          </SectionHeader>

          <MenuGrid>
            {menuData.items.map((item, index) => (
              <MenuItem
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
              >
                <ItemHeader>
                  <h3>{getTranslations(item.id, 'name').mainText}</h3>
                  <span className="price">{item.price}</span>
                </ItemHeader>
                <MultiLanguageText>
                  <div className="main-text">{getTranslations(item.id, 'description').mainText}</div>
                  {getTranslations(item.id, 'description').secondaryTexts.map((item, index) => (
                    <div 
                      key={index} 
                      className={item.lang === 'en' ? 'english-text' : 'secondary-text'}
                    >
                      {item.text}
                    </div>
                  ))}
                </MultiLanguageText>
                {item.unit && (
                  <ItemNote>{t('menu.pricePer')} {item.unit}</ItemNote>
                )}
                {item.note && (
                  <ItemNote>{item.note}</ItemNote>
                )}
              </MenuItem>
            ))}
          </MenuGrid>

          {menuData.note && (
            <SectionNote>
              <p>{menuType === 'pratos' ? t('menu.pratosNote') : menuData.note}</p>
            </SectionNote>
          )}
        </MenuSection>
      </Container>
    </MenuPageContainer>
  );
};

export default MenuPage;
